/**
 * Componente AutomationKanbanModal - Modal de visualização do Kanban de Automações
 * Exibe automações organizadas por fase do pipeline (inspirado no Bitrix24)
 * 
 * RELEASE: 1.0.0 - 2026-01-26 - Modal inicial para visualização de automações
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, Loader2, LayoutGrid } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { usePipelinePhaseStats } from '@/hooks/usePipelinePhaseStats';
import { usePhaseAutomations } from '@/hooks/usePhaseAutomations';
import { AutomationKanbanColumn } from './AutomationKanbanColumn';

// Props do componente
interface AutomationKanbanModalProps {
  /** Controla se o modal está aberto */
  open: boolean;
  /** Callback para alterar estado de abertura */
  onOpenChange: (open: boolean) => void;
  /** ID do pipeline para exibir automações */
  pipelineId: string | null;
  /** Nome do pipeline para exibir no header */
  pipelineName: string;
}

/**
 * Modal de Kanban de Automações
 * Exibe todas as fases do pipeline com suas automações configuradas
 */
export function AutomationKanbanModal({
  open,
  onOpenChange,
  pipelineId,
  pipelineName,
}: AutomationKanbanModalProps) {
  const navigate = useNavigate();

  // Buscar fases do pipeline
  const { phases, isLoading: phasesLoading } = usePipelinePhaseStats(pipelineId);

  // Buscar automações por fase (com helpers por categoria)
  const { 
    getTriggerAutomations,
    getActionAutomations,
    totalAutomations,
    isLoading: automationsLoading 
  } = usePhaseAutomations(pipelineId);

  // Handler para criar novo fluxo com fase pré-selecionada
  const handleCreateFlow = (phaseId: string) => {
    // Navega para o flow builder com query params para pré-configurar
    // O flow builder pode ler esses params e pré-selecionar a fase no trigger
    navigate(`/tenant/flows?new=true&preset_phase=${phaseId}&pipeline=${pipelineId}`);
    onOpenChange(false);
  };

  // Handler para editar fluxo existente
  const handleEditFlow = (flowId: string) => {
    navigate(`/tenant/flows/${flowId}`);
    onOpenChange(false);
  };

  // Loading combinado
  const isLoading = phasesLoading || automationsLoading;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] w-[95vw] h-[85vh] p-0 gap-0">
        {/* Header fixo */}
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/10">
              <Zap className="h-5 w-5 text-amber-500" />
            </div>
            <div className="flex-1">
              <DialogTitle className="text-lg font-semibold">
                Regras de Automação e Gatilhos
              </DialogTitle>
              <DialogDescription className="flex items-center gap-2 mt-0.5">
                <LayoutGrid className="h-3.5 w-3.5" />
                Pipeline: {pipelineName}
                {totalAutomations > 0 && (
                  <Badge variant="secondary" className="ml-2 h-5 text-[10px]">
                    {totalAutomations} automação{totalAutomations !== 1 ? 'ões' : ''}
                  </Badge>
                )}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Conteúdo com scroll horizontal */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {isLoading ? (
            // Loading state
            <div className="flex items-center justify-center h-full">
              <div className="flex flex-col items-center gap-3 text-muted-foreground">
                <Loader2 className="h-8 w-8 animate-spin" />
                <span className="text-sm">Carregando automações...</span>
              </div>
            </div>
          ) : phases.length === 0 ? (
            // Empty state - Nenhuma fase
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <LayoutGrid className="h-12 w-12 mb-3 opacity-30" />
              <p className="text-sm">Nenhuma fase configurada neste pipeline</p>
            </div>
          ) : (
            // Kanban de automações
            <ScrollArea className="h-full">
              <div className="flex gap-4 p-6 h-[calc(85vh-100px)]">
                {phases.map((phase) => (
                  <AutomationKanbanColumn
                    key={phase.phase_id}
                    phaseId={phase.phase_id}
                    phaseName={phase.phase_name}
                    phaseColor={phase.phase_color}
                    triggerAutomations={getTriggerAutomations(phase.phase_id)}
                    actionAutomations={getActionAutomations(phase.phase_id)}
                    onCreateFlow={handleCreateFlow}
                    onEditFlow={handleEditFlow}
                  />
                ))}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
